import { createSignal, Show, onMount, onCleanup, createEffect } from 'solid-js'
import FAQContent from './FAQContent'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import { userStore } from '/web/store'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { toastStore } from '/web/store'
import { setComponentPageTitle } from '/web/shared/util'
import { getAvailableTokenOptions } from '/common/token-plans'

const PaymentModal = (props: { show: boolean; onClose: () => void; url: string }) => {
  return (
    <Show when={props.show}>
      <div class="fixed inset-0 z-[200] flex items-center justify-center">
        {/* Overlay */}
        <div class="fixed inset-0 bg-black/80" onClick={props.onClose} />

        {/* Modal */}
        <div class="relative z-[201] h-[100vh] w-full md:h-[850px] md:w-[90vw] md:max-w-4xl">
          {/* Modal content with dark background */}
          <div class="h-full w-full rounded-lg bg-main pt-[30px]">
            {/* Close button */}
            <button
              onClick={props.onClose}
              class="absolute right-4 top-4 z-[202] flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-black hover:bg-gray-300"
            >
              ✕
            </button>

            {/* iframe container */}
            <div class="h-[calc(100vh-30px)] w-full md:h-[820px]">
              <iframe src={props.url} class="h-full w-full" style="border: 0;" allow="payment" />
            </div>
          </div>
        </div>
      </div>
    </Show>
  )
}

const BuyTokensModal = () => {
  const state = userStore()
  const [selectedTokens, setSelectedTokens] = createSignal<number>(0)
  const [showPayment, setShowPayment] = createSignal(false)
  const [paymentUrl, setPaymentUrl] = createSignal('')
  const [hasClaimedInitial, setHasClaimedInitial] = createSignal(
    state.profile?.hasClaimedInitialTokens ?? false
  )

  const closeModal = () => {
    userStore.buyTokensModal(false)
    setShowPayment(false)
  }

  const purchase = async () => {
    const option = getAvailableTokenOptions(hasClaimedInitial()).find(
      (opt) => opt.amount === selectedTokens()
    )
    if (!option) return

    try {
      const response = await aifansSubscriptionApi.createTokenReloadLink(
        option.price,
        option.id,
        option.amount
      )

      if (response.result?.redirectURL) {
        setPaymentUrl(response.result.redirectURL)
        setShowPayment(true)
      }
    } catch (error) {
      toastStore.error('There was a problem processing your request. Please try again.')
    }
  }

  createEffect(() => {
    const hasClaimedInitial = state.profile?.hasClaimedInitialTokens ?? false
    setHasClaimedInitial(hasClaimedInitial)
    setSelectedTokens(getAvailableTokenOptions(hasClaimedInitial)[0].amount)
  })

  onMount(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'payment-success') {
        setShowPayment(false)
        toastStore.success('Payment successful! Your tokens have been added.')
        userStore.buyTokensModal(false)
      }
    }

    window.addEventListener('message', handleMessage)
    onCleanup(() => window.removeEventListener('message', handleMessage))
  })

  return (
    <Show when={state.showBuyTokens}>
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center bg-black/80"
        onClick={closeModal}
      >
        <div
          class={`relative mx-2 w-full ${
            showPayment() ? 'max-w-[560px]' : 'max-w-[900px]'
          } rounded-xl bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200 p-[2px] md:mx-4`}
          onClick={(e) => e.stopPropagation()}
        >
          <div class="relative h-full w-full rounded-lg bg-[#0B0F11] p-3 md:p-6">
            <button
              onClick={closeModal}
              class="absolute right-4 top-4 text-2xl text-gray-400 hover:text-white md:right-6 md:top-6"
            >
              ✕
            </button>

            <Show
              when={!showPayment()}
              fallback={
                <div class="h-[740px] max-h-[calc(100vh-200px)] w-full">
                  <iframe
                    src={paymentUrl()}
                    class="h-full w-full"
                    style="border: 0;"
                    allow="payment"
                  />
                </div>
              }
            >
              <div class="flex flex-col px-1 md:px-0">
                <div class="grid gap-8 md:grid-cols-2">
                  {/* Left column */}
                  <div>
                    <h3 class="mb-4 text-center text-xl font-bold md:text-left">Buy Tokens</h3>
                    <div class="space-y-4">
                      {getAvailableTokenOptions(hasClaimedInitial()).map((option) => (
                        <button
                          onClick={() => setSelectedTokens(option.amount)}
                          class="flex w-full items-center rounded-full bg-[#222] p-3 pl-6 pr-8 hover:bg-[#333]"
                        >
                          <div class="grid w-full grid-cols-[auto_50px_1fr_auto] items-center gap-4">
                            <div
                              class={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                selectedTokens() === option.amount
                                  ? 'border-cosplay-purple'
                                  : 'border-gray-500'
                              }`}
                            >
                              {selectedTokens() === option.amount && (
                                <div class="h-3 w-3 rounded-full bg-cosplay-purple" />
                              )}
                            </div>
                            <span class="text-2xl font-bold">{option.amount}</span>
                            <span>TOKENS</span>
                            <span>{`for $${option.price}`}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Right column */}
                  <div>
                    <h3 class="mb-4 text-center text-xl font-bold md:text-left">Payment Options</h3>
                    <div class="rounded-full bg-[#222] p-4 px-6">
                      <div class="flex items-center gap-2">
                        <div class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-cosplay-purple">
                          <div class="h-3 w-3 rounded-full bg-cosplay-purple" />
                        </div>
                        <span class="text-sm md:text-base">Credit/Debit Card</span>
                        <div class="ml-auto flex items-center gap-1 md:gap-3">
                          <img
                            src="/images/footer/visa.svg"
                            alt="Visa"
                            class="h-[10px] w-auto brightness-0 invert md:h-[12px]"
                          />
                          <img
                            src="/images/footer/mc-circles.png"
                            alt="Mastercard"
                            class="h-[10px] w-auto md:h-[12px]"
                          />
                          <img
                            src="/images/footer/discover-white.png"
                            alt="Discover"
                            class="h-[10px] w-auto md:h-[12px]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-8 flex justify-center">
                  <button
                    onClick={purchase}
                    class="rounded-lg bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200 px-12 py-3 text-lg font-medium hover:opacity-90"
                  >
                    Continue to Checkout
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default BuyTokensModal
